import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import {
  signOut,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import Avatar, { PRESET_AVATARS } from './Avatar';
import { useLanguage } from '../i18n';
import { LANGUAGES } from '../i18n/translations';
import { TIMEZONES, getTimezoneLabel } from '../services/timezones';
import {
  getPushStatus,
  enablePush,
  disablePush,
  isPushTurnedOff,
  setPushTurnedOff,
} from '../push';

const SELECTABLE_AVATARS = Object.keys(PRESET_AVATARS).filter(id => id !== 'unveiled');

const PAYSTACK_PUBLIC_KEY = 'pk_live_ced368f17237c0a072b6db63d641ca539b9ca6be';
const MONTHLY_PLAN_CODE = 'PLN_fdt81n4wnr0z85q';
const SIX_MONTH_PLAN_CODE = 'PLN_prih2qg54fputl9';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .settings-avatar-btn {
    transition: all 0.15s !important;
  }
  .settings-avatar-btn:hover {
    border-color: #008081 !important;
    transform: translateY(-2px) !important;
  }
  .settings-theme-btn {
    transition: all 0.15s !important;
  }
  .settings-theme-btn:hover {
    border-color: #008081 !important;
  }
  .settings-lang-btn {
    transition: all 0.15s !important;
  }
  .settings-lang-btn:hover {
    border-color: #008081 !important;
  }
  .settings-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .settings-save-btn {
    transition: transform 0.2s, box-shadow 0.2s, background-color 0.2s !important;
  }
  .settings-save-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.35) !important;
  }
  .settings-premium-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .settings-premium-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
  }
  .settings-delete-btn {
    transition: background-color 0.2s !important;
  }
  .settings-delete-btn:hover {
    background-color: rgba(224,92,92,0.12) !important;
  }
  .settings-legal-link {
    transition: color 0.2s !important;
  }
  .settings-legal-link:hover {
    color: #008081 !important;
  }
  .settings-cancel-sub-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .settings-cancel-sub-btn:hover:not(:disabled) {
    background-color: rgba(224,92,92,0.1) !important;
    border-color: rgba(224,92,92,0.4) !important;
  }
`;

export default function SettingsView({ user, userData, setUserData, theme, setTheme, t }) {
  const { t: translate, language, setLanguage } = useLanguage();
  const [selectedAvatar, setSelectedAvatar] = useState(userData?.avatarId || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  // Timezone change (goes through the changeTimezone Cloud Function —
  // never written straight to Firestore from here, so the server can
  // enforce the "no changing after using today's limits" rule)
  const [selectedTz, setSelectedTz] = useState(userData?.timezone || '');
  const [tzSaving, setTzSaving] = useState(false);
  const [tzSaved, setTzSaved] = useState(false);
  const [tzError, setTzError] = useState('');

  // Cancellation flow
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Push notifications (this device)
  const [pushStatus, setPushStatus] = useState('loading');
  const [pushOff, setPushOff] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  const dark = theme === 'dark';

  // translate() returns the key itself on a miss, so check for that.
  const tr = (key, fallback) => {
    const v = translate(key);
    return !v || v === key ? fallback : v;
  };

  // How this user signs in decides how we re-verify them before deleting.
  // Password users re-enter their password; Google-only users get the Google popup.
  const providerIds = auth.currentUser?.providerData?.map(p => p.providerId) || [];
  const usesPassword = providerIds.includes('password');

  useEffect(() => {
    if (window.PaystackPop) { setPaystackLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getPushStatus().then((s) => { if (!cancelled) setPushStatus(s); });
    setPushOff(isPushTurnedOff());
    return () => { cancelled = true; };
  }, []);

  // Keep the dropdown in step with the saved timezone (e.g. when userData
  // loads after this screen opens, or the server confirms a change).
  useEffect(() => {
    setSelectedTz(userData?.timezone || '');
  }, [userData?.timezone]);

  const colors = {
    cardBg:         dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:     dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    text:           dark ? '#ffffff' : '#1B2D44',
    subtext:        dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    mutedText:      dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    labelColor:     dark ? 'rgba(255,255,255,0.35)' : '#6b7280',
    inputBg:        dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder:    dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    inputColor:     dark ? '#ffffff' : '#1B2D44',
    optionBg:       dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    optionBorder:   dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    optionText:     dark ? '#ffffff' : '#1B2D44',
    optionSubtext:  dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    previewBg:      dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    previewBorder:  dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    previewSubtext: dark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
    avatarLabelInactive: dark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
    legalLinkColor: dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    cancelBtnBorder: dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
    cancelBtnText:  dark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    counterColor:   dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    perkBg:         dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    perkBorder:     dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    perkText:       dark ? '#ffffff' : '#1B2D44',
    perkSub:        dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    goalsBg:        dark ? 'rgba(0,128,129,0.08)' : 'rgba(0,128,129,0.05)',
    goalsBorder:    dark ? 'rgba(0,128,129,0.15)' : 'rgba(0,128,129,0.2)',
    goalsSubtext:   dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
  };

  const isUnveiled       = userData?.unveiled || userData?.activeVeyl?.unveiled;
  const hasAvatarChanged = selectedAvatar !== (userData?.avatarId || null);
  const goalsAchieved    = userData?.goalsAchieved || 0;
  const displayAvatarId  = isUnveiled ? 'unveiled' : selectedAvatar;
  const isPremium        = userData?.isPremium || false;
  const premiumPlan      = userData?.premiumPlan || null;
  const hasTzChanged     = !!selectedTz && selectedTz !== (userData?.timezone || '');

  const card = {
    background: colors.cardBg,
    backdropFilter: 'blur(10px)',
    borderRadius: '1.25rem',
    padding: '1.75rem',
    border: `1px solid ${colors.cardBorder}`,
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: '0.75rem',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    backgroundColor: colors.inputBg,
    color: colors.inputColor,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  const sectionLabel = {
    fontSize: '0.72rem',
    fontWeight: '700',
    color: colors.labelColor,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '1rem',
    display: 'block'
  };

  const handleUpgrade = (planCode, planName) => {
    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is loading, please try again in a moment.');
      return;
    }

    const amount = planCode === MONTHLY_PLAN_CODE ? 64500 : 258000;

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: user.email,
      amount: amount,
      currency: 'KES',
      plan: planCode,
      metadata: {
        custom_fields: [
          { display_name: 'User ID',   variable_name: 'userId',   value: user.uid },
          { display_name: 'Plan Name', variable_name: 'planName', value: planName }
        ]
      },
      callback: function(response) {
        console.log('Payment successful:', response.reference);
      },
      onClose: function() {}
    });

    handler.openIframe();
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      await cancelSubscription();

      // Update local state immediately — Firestore onSnapshot will also fire
      setUserData(prev => ({
        ...prev,
        isPremium: false,
        premiumPlan: null,
        paystackSubscriptionCode: '',
      }));

      setCancelSuccess(true);
      setShowCancelConfirm(false);
    } catch (err) {
      setCancelError(err.message || 'Failed to cancel. Please contact support@veyl.space.');
    } finally {
      setCancelling(false);
    }
  };

  const handleTurnOnPush = async () => {
    setPushBusy(true);
    setPushError('');
    const result = await enablePush();
    setPushBusy(false);
    if (result === 'error') {
      setPushError(tr('pushError', 'Something went wrong turning notifications on. Please try again.'));
    } else {
      setPushStatus(result);
      if (result === 'granted') setPushOff(false);
    }
  };

  const handleTurnOffPush = async () => {
    setPushBusy(true);
    setPushError('');
    await disablePush();
    setPushTurnedOff(true);
    setPushOff(true);
    setPushBusy(false);
  };

  const handleSaveAvatar = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { avatarId: selectedAvatar });
      setUserData(prev => ({ ...prev, avatarId: selectedAvatar }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(translate('errorSavingAvatar') + ': ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveName = async () => {
    setNameError('');
    const trimmed = newName.trim();
    if (!trimmed) { setNameError(translate('nicknameRequired')); return; }
    if (trimmed.length < 2) { setNameError(translate('nicknameTooShort')); return; }
    if (trimmed.length > 20) { setNameError(translate('nicknameTooLong')); return; }
    setNameSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { name: trimmed });
      setUserData(prev => ({ ...prev, name: trimmed }));
      setNameSaved(true);
      setNewName('');
      setTimeout(() => setNameSaved(false), 2500);
    } catch (err) {
      setNameError(translate('errorSavingNickname') + ': ' + err.message);
    } finally {
      setNameSaving(false);
    }
  };

  const handleLanguageChange = async (code) => {
    setLanguage(code);
    try {
      await updateDoc(doc(db, 'users', user.uid), { language: code });
    } catch (err) {
      console.error('Error saving language:', err);
    }
  };

  // Timezone change. The Cloud Function (changeTimezone) is the only thing
  // that is allowed to write the timezone after onboarding. It checks the
  // lock rules (no change after using today's messages/voice notes, and a
  // cooldown between changes) and throws a friendly 'failed-precondition'
  // error when the change isn't allowed — that message is shown as-is.
  const handleSaveTimezone = async () => {
    if (!hasTzChanged) return;
    setTzError('');
    setTzSaving(true);
    try {
      const functions = getFunctions();
      const changeTimezone = httpsCallable(functions, 'changeTimezone');
      await changeTimezone({ timezone: selectedTz });

      // Mirror the change locally right away; Firestore onSnapshot will
      // also confirm it.
      setUserData(prev => ({
        ...prev,
        timezone: selectedTz,
        activeVeyl: prev?.activeVeyl
          ? { ...prev.activeVeyl, timezone: selectedTz }
          : prev?.activeVeyl,
      }));

      setTzSaved(true);
      setTimeout(() => setTzSaved(false), 2500);
    } catch (err) {
      console.error('Timezone change failed:', err.code, err.message);
      if (err?.code === 'functions/failed-precondition') {
        // Our own function's message — already written for the user.
        setTzError(err.message);
      } else if (err?.code === 'functions/invalid-argument') {
        setTzError(tr('timezoneInvalid', 'That timezone is not valid. Please pick one from the list.'));
      } else {
        setTzError(tr('timezoneChangeError', 'Could not change your timezone. Please try again.'));
      }
      // Put the dropdown back to the saved value so it never looks changed
      // when it wasn't.
      setSelectedTz(userData?.timezone || '');
    } finally {
      setTzSaving(false);
    }
  };

  // Turns Firebase error codes into plain messages. Never shows raw Firebase text.
  const getDeleteErrorMessage = (err) => {
    switch (err?.code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'That password is incorrect. Please try again.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Google sign-in was cancelled. Your account has not been deleted.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/network-request-failed':
        return 'Network problem. Check your connection and try again.';
      case 'auth/requires-recent-login':
        return 'For your security, please sign out, sign back in, and try again.';
      // Messages from our own deleteAccount Cloud Function are already user-friendly.
      case 'functions/failed-precondition':
        return err.message;
      default:
        return translate('errorDeletingAccount') + '. Please try again or contact support@veyl.space.';
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setDeleteError('');

    // An active subscription keeps billing after the account is gone, so block it.
    if (isPremium) {
      setDeleteError('You have an active subscription. Please cancel it first (Premium card above), then delete your account.');
      return;
    }

    if (usesPassword && !deletePassword) {
      setDeleteError('Enter your password to confirm.');
      return;
    }

    setDeleting(true);

    // STEP 1: re-verify the user. Nothing is deleted until this succeeds.
    try {
      if (usesPassword) {
        const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
        await reauthenticateWithCredential(currentUser, credential);
      } else {
        await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
      }
      // Force a fresh token so the server sees the new sign-in time.
      await currentUser.getIdToken(true);
    } catch (err) {
      console.error('Re-authentication failed:', err.code, err.message);
      setDeleteError(getDeleteErrorMessage(err));
      setDeleting(false);
      return;
    }

    // STEP 2: the server deletes everything (Firestore, Storage, Rasa) and then the login.
    try {
      const functions = getFunctions();
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn();
      await signOut(auth);
    } catch (err) {
      console.error('Account deletion failed:', err.code, err.message);
      setDeleteError(getDeleteErrorMessage(err));
      setDeleting(false);
    }
  };

  const pushIsOn = pushStatus === 'granted' && !pushOff;

  return (
    <div style={{ maxWidth: '36rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'fadeInUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}>
      <style>{styleTag}</style>

      {/* ── Premium card ── */}
      <div style={{
        ...card,
        border: isPremium ? '1px solid rgba(0,128,129,0.35)' : `1px solid ${colors.cardBorder}`,
        background: isPremium
          ? 'linear-gradient(145deg, rgba(0,128,129,0.12), rgba(75,44,94,0.08))'
          : colors.cardBg
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{
            width: '40px', height: '40px',
            background: 'linear-gradient(135deg, #008081, #4B2C5E)',
            borderRadius: '0.75rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.25rem', flexShrink: 0,
            boxShadow: '0 4px 12px rgba(0,128,129,0.3)'
          }}>✨</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '800', color: colors.text, margin: 0, letterSpacing: '-0.01em' }}>
              {isPremium ? translate('premiumTitle') : translate('upgradeTitle')}
            </h3>
            {isPremium && (
              <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#008081', backgroundColor: 'rgba(0,128,129,0.15)', padding: '0.15rem 0.5rem', borderRadius: '9999px', display: 'inline-block', marginTop: '0.2rem' }}>
                {premiumPlan === 'sixMonth' ? translate('sixMonthPlan') : translate('monthlyPlan')}
              </span>
            )}
          </div>
        </div>

        {isPremium ? (
          <>
            <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
              {translate('premiumActiveDesc')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1.25rem' }}>
              {[
                { icon: '💬', labelKey: 'unlimitedMessages' },
                { icon: '🎙️', labelKey: 'unlimitedVoice' },
                { icon: '👥', labelKey: 'unlimitedGroup' },
                { icon: '🌐', labelKey: 'unlimitedCommunity' },
              ].map((perk, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', backgroundColor: 'rgba(0,128,129,0.1)', border: '1px solid rgba(0,128,129,0.15)', borderRadius: '0.75rem' }}>
                  <span style={{ fontSize: '1rem' }}>{perk.icon}</span>
                  <span style={{ fontSize: '0.78rem', color: colors.perkText, fontWeight: '500' }}>{translate(perk.labelKey)}</span>
                </div>
              ))}
            </div>

            {/* Cancel subscription section */}
            {cancelSuccess ? (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: '0.875rem', textAlign: 'center', animation: 'fadeIn 0.3s ease' }}>
                <div style={{ fontSize: '1.25rem', marginBottom: '0.35rem' }}>✓</div>
                <p style={{ fontSize: '0.85rem', color: '#16a34a', margin: 0, fontWeight: '600' }}>
                  Subscription cancelled. You'll keep access until the end of your billing period.
                </p>
              </div>
            ) : !showCancelConfirm ? (
              <button
                className="settings-cancel-sub-btn"
                onClick={() => { setShowCancelConfirm(true); setCancelError(''); }}
                style={{
                  width: '100%', padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                  borderRadius: '0.875rem',
                  color: dark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500',
                }}
              >
                Cancel subscription
              </button>
            ) : (
              <div style={{ animation: 'fadeIn 0.2s ease' }}>
                <div style={{ padding: '1rem', backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '0.875rem', marginBottom: '0.875rem' }}>
                  <p style={{ fontSize: '0.85rem', color: '#f87171', margin: 0, lineHeight: 1.6, fontWeight: '500' }}>
                    Are you sure? Your premium access will end at the close of your current billing period. This cannot be undone.
                  </p>
                </div>
                {cancelError && (
                  <p style={{ fontSize: '0.8rem', color: '#f87171', margin: '0 0 0.75rem', textAlign: 'center' }}>
                    {cancelError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <button
                    onClick={() => { setShowCancelConfirm(false); setCancelError(''); }}
                    disabled={cancelling}
                    style={{
                      flex: 1, padding: '0.75rem',
                      border: `1px solid ${colors.cancelBtnBorder}`,
                      backgroundColor: 'transparent',
                      color: colors.cancelBtnText,
                      borderRadius: '0.875rem', fontWeight: '600',
                      cursor: cancelling ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', opacity: cancelling ? 0.5 : 1,
                    }}
                  >
                    Keep premium
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    style={{
                      flex: 1, padding: '0.75rem',
                      backgroundColor: '#E05C5C',
                      color: '#FFFFFF', border: 'none',
                      borderRadius: '0.875rem', fontWeight: '700',
                      cursor: cancelling ? 'not-allowed' : 'pointer',
                      opacity: cancelling ? 0.6 : 1,
                      fontSize: '0.875rem',
                      boxShadow: cancelling ? 'none' : '0 4px 12px rgba(224,92,92,0.3)',
                    }}
                  >
                    {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
              {translate('upgradeDesc')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1.25rem' }}>
              {[
                { icon: '💬', labelKey: 'unlimitedMessages', subKey: 'was10day' },
                { icon: '🎙️', labelKey: 'unlimitedVoice',    subKey: 'was5day' },
                { icon: '👥', labelKey: 'unlimitedGroup',    subKey: 'was20day' },
                { icon: '🌐', labelKey: 'unlimitedCommunity', subKey: 'was30day' },
              ].map((perk, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', backgroundColor: colors.perkBg, borderRadius: '0.75rem', border: `1px solid ${colors.perkBorder}` }}>
                  <span style={{ fontSize: '1rem' }}>{perk.icon}</span>
                  <div>
                    <div style={{ fontSize: '0.78rem', color: colors.perkText, fontWeight: '500' }}>{translate(perk.labelKey)}</div>
                    <div style={{ fontSize: '0.68rem', color: colors.perkSub }}>{translate(perk.subKey)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <button
                className="settings-premium-btn"
                onClick={() => handleUpgrade(MONTHLY_PLAN_CODE, 'monthly')}
                disabled={!paystackLoaded}
                style={{ padding: '1rem', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', textAlign: 'center', boxShadow: '0 4px 15px rgba(0,128,129,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}
              >
                <div style={{ fontSize: '1rem' }}>$5 / month</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: '0.2rem' }}>{translate('billedMonthly')}</div>
              </button>
              <button
                className="settings-premium-btn"
                onClick={() => handleUpgrade(SIX_MONTH_PLAN_CODE, 'sixMonth')}
                disabled={!paystackLoaded}
                style={{ padding: '1rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', textAlign: 'center', position: 'relative', boxShadow: '0 4px 15px rgba(75,44,94,0.35)', opacity: paystackLoaded ? 1 : 0.7 }}
              >
                <div style={{ position: 'absolute', top: '-10px', right: '10px', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#fff', fontSize: '0.6rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '9999px' }}>
                  {translate('bestValue')}
                </div>
                <div style={{ fontSize: '1rem' }}>$20 / 6 months</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: '0.2rem' }}>{translate('perSixMonths')}</div>
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Avatar card ── */}
      <div style={card}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {translate('settingsTitle')}
        </h2>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.5rem', lineHeight: 1.6 }}>
          {translate('settingsDesc')}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', backgroundColor: colors.previewBg, borderRadius: '0.875rem', border: `1px solid ${colors.previewBorder}` }}>
          <Avatar username={userData?.name} avatarId={displayAvatarId} size={52} />
          <div>
            <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.95rem' }}>{userData?.name}</div>
            <div style={{ fontSize: '0.75rem', color: colors.previewSubtext, marginTop: '0.1rem' }}>
              {isUnveiled ? translate('unveiledLocked') : translate('howOthersSee')}
            </div>
          </div>
        </div>

        {isUnveiled ? (
          <div style={{ padding: '1rem', backgroundColor: 'rgba(192,132,252,0.1)', border: '1px solid rgba(192,132,252,0.2)', borderRadius: '0.875rem', fontSize: '0.875rem', color: '#c084fc', textAlign: 'center', lineHeight: 1.6 }}>
            {translate('unveiledNote')}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={sectionLabel}>{translate('chooseAvatar')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.6rem' }}>
                {SELECTABLE_AVATARS.map(id => (
                  <button
                    key={id}
                    className="settings-avatar-btn"
                    onClick={() => setSelectedAvatar(id)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
                      padding: '0.75rem 0.5rem',
                      border: selectedAvatar === id ? '2px solid #008081' : `2px solid ${colors.optionBorder}`,
                      backgroundColor: selectedAvatar === id ? 'rgba(0,128,129,0.12)' : colors.optionBg,
                      borderRadius: '0.875rem', cursor: 'pointer'
                    }}
                  >
                    <Avatar username={id} avatarId={id} size={40} />
                    <span style={{ fontSize: '0.68rem', color: selectedAvatar === id ? '#008081' : colors.avatarLabelInactive, fontWeight: selectedAvatar === id ? '700' : '400' }}>
                      {translate(`avatar_${id}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <button
              className="settings-save-btn"
              onClick={handleSaveAvatar}
              disabled={saving || !hasAvatarChanged}
              style={{
                width: '100%', padding: '0.875rem',
                background: saved ? 'linear-gradient(135deg, #16a34a, #15803d)' : 'linear-gradient(135deg, #008081, #005f5f)',
                color: '#FFFFFF', border: 'none', borderRadius: '0.75rem',
                fontWeight: '700', fontSize: '0.95rem',
                cursor: (saving || !hasAvatarChanged) ? 'not-allowed' : 'pointer',
                opacity: (saving || !hasAvatarChanged) ? 0.5 : 1,
                boxShadow: (saving || !hasAvatarChanged) ? 'none' : '0 4px 15px rgba(0,128,129,0.3)'
              }}
            >
              {saved ? '✓ ' + translate('saved') : saving ? translate('saving') : translate('saveAvatar')}
            </button>
          </>
        )}
      </div>

      {/* ── Theme card ── */}
      <div style={card}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {translate('appearance')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {translate('appearanceDesc')}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {[
            { value: 'light', labelKey: 'lightMode', emoji: '☀️' },
            { value: 'dark',  labelKey: 'darkMode',  emoji: '🌙' },
          ].map(opt => (
            <button
              key={opt.value}
              className="settings-theme-btn"
              onClick={() => setTheme(opt.value)}
              style={{
                padding: '1rem',
                border: theme === opt.value ? '2px solid #008081' : `2px solid ${colors.optionBorder}`,
                backgroundColor: theme === opt.value ? 'rgba(0,128,129,0.12)' : colors.optionBg,
                borderRadius: '0.875rem', cursor: 'pointer', textAlign: 'left'
              }}
            >
              <div style={{ fontSize: '1.25rem', marginBottom: '0.4rem' }}>{opt.emoji}</div>
              <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.875rem' }}>
                {translate(opt.labelKey)}
              </div>
              {theme === opt.value && (
                <div style={{ marginTop: '0.3rem', fontSize: '0.68rem', color: '#008081', fontWeight: '700' }}>
                  ✓ {translate('active2')}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Push notifications card (this device) ── */}
      {pushStatus !== 'loading' && (
        <div style={card}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
            {tr('pushSettingsTitle', 'Notifications on this device')}
          </h3>
          <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
            {tr('pushSettingsDesc', "Get an alert when you're matched or your partner messages you, even when Veyl is closed. Alerts never include message content. This setting applies to this device only.")}
          </p>

          {pushStatus === 'unsupported' && (
            <p style={{ fontSize: '0.8rem', color: colors.subtext, margin: 0, lineHeight: 1.6 }}>
              {tr('pushSettingsUnsupported', "Notifications aren't available in this browser. On iPhone or iPad, add Veyl to your Home Screen first (Share, then Add to Home Screen), then open it from there.")}
            </p>
          )}

          {pushStatus === 'denied' && (
            <p style={{ fontSize: '0.8rem', color: '#f87171', margin: 0, lineHeight: 1.6 }}>
              {tr('pushSettingsBlocked', "Notifications are blocked for Veyl in your browser. To turn them on, open your browser's site settings for veyl.space, allow notifications, then reload the page.")}
            </p>
          )}

          {(pushStatus === 'default' || (pushStatus === 'granted' && pushOff)) && (
            <button
              className="settings-save-btn"
              onClick={handleTurnOnPush}
              disabled={pushBusy}
              style={{
                width: '100%', padding: '0.875rem',
                background: 'linear-gradient(135deg, #008081, #005f5f)',
                color: '#FFFFFF', border: 'none', borderRadius: '0.75rem',
                fontWeight: '700', fontSize: '0.95rem',
                cursor: pushBusy ? 'not-allowed' : 'pointer',
                opacity: pushBusy ? 0.5 : 1,
                boxShadow: pushBusy ? 'none' : '0 4px 15px rgba(0,128,129,0.3)'
              }}
            >
              {pushBusy ? tr('pushSettingsWorking', 'Working…') : tr('pushEnable', 'Turn on notifications')}
            </button>
          )}

          {pushIsOn && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem', padding: '0.75rem 1rem', backgroundColor: 'rgba(0,128,129,0.1)', border: '1px solid rgba(0,128,129,0.2)', borderRadius: '0.75rem' }}>
                <span style={{ color: '#008081', fontWeight: '700' }}>✓</span>
                <span style={{ fontSize: '0.85rem', color: colors.text, fontWeight: '600' }}>
                  {tr('pushSettingsOn', 'Notifications are on for this device')}
                </span>
              </div>
              <button
                className="settings-cancel-sub-btn"
                onClick={handleTurnOffPush}
                disabled={pushBusy}
                style={{
                  width: '100%', padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.cancelBtnBorder}`,
                  borderRadius: '0.875rem',
                  color: colors.cancelBtnText,
                  cursor: pushBusy ? 'not-allowed' : 'pointer',
                  opacity: pushBusy ? 0.5 : 1,
                  fontSize: '0.85rem', fontWeight: '500',
                }}
              >
                {pushBusy ? tr('pushSettingsWorking', 'Working…') : tr('pushSettingsTurnOff', 'Turn off notifications')}
              </button>
            </div>
          )}

          {pushError && (
            <p style={{ fontSize: '0.8rem', color: '#f87171', margin: '0.75rem 0 0', lineHeight: 1.5 }}>
              {pushError}
            </p>
          )}
        </div>
      )}

      {/* ── Language card ── */}
      <div style={card}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {translate('languageLabel')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {translate('languageDesc')}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className="settings-lang-btn"
              onClick={() => handleLanguageChange(lang.code)}
              style={{
                padding: '0.75rem',
                border: language === lang.code ? '2px solid #008081' : `2px solid ${colors.optionBorder}`,
                backgroundColor: language === lang.code ? 'rgba(0,128,129,0.12)' : colors.optionBg,
                borderRadius: '0.875rem', cursor: 'pointer', textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: '0.1rem'
              }}
            >
              <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.85rem' }}>{lang.nativeName}</div>
              <div style={{ fontSize: '0.7rem', color: colors.optionSubtext }}>{lang.name}</div>
              {language === lang.code && (
                <div style={{ fontSize: '0.65rem', color: '#008081', fontWeight: '700' }}>✓</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Timezone card ── */}
      <div style={card}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {tr('timezoneTitle', 'Timezone')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {tr('timezoneCurrentIs', 'Your current timezone is')}{' '}
          <strong style={{ color: colors.text }}>
            {userData?.timezone ? getTimezoneLabel(userData.timezone) : '—'}
          </strong>.
        </p>

        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.875rem', backgroundColor: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.2)', borderRadius: '0.875rem', marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '1rem', flexShrink: 0 }}>🕛</span>
          <p style={{ fontSize: '0.8rem', color: '#facc15', lineHeight: 1.6, margin: 0 }}>
            {tr('timezoneLockNote', "Your daily message and voice-note limits reset at midnight in your timezone. To keep this fair, you can only change your timezone on a day when you haven't used any messages or voice notes, and only once every 7 days.")}
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={sectionLabel}>{tr('timezoneNew', 'Choose timezone')}</label>
          <select
            className="settings-input"
            value={selectedTz}
            onChange={(e) => { setSelectedTz(e.target.value); setTzError(''); }}
            disabled={tzSaving}
            style={{
              ...inputStyle,
              appearance: 'none',
              borderColor: tzError ? '#f87171' : colors.inputBorder,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${dark ? 'rgba(255,255,255,0.4)' : 'rgba(27,45,68,0.5)'}' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              paddingRight: '2.5rem'
            }}
          >
            {!userData?.timezone && (
              <option value="" style={{ backgroundColor: dark ? '#1a1f2e' : '#ffffff', color: colors.inputColor }}>
                {tr('selectTimezone', 'Select timezone')}
              </option>
            )}
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value} style={{ backgroundColor: dark ? '#1a1f2e' : '#ffffff', color: colors.inputColor }}>
                {getTimezoneLabel(tz.value)}
              </option>
            ))}
          </select>
          {tzError && (
            <p style={{ fontSize: '0.78rem', color: '#f87171', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
              {tzError}
            </p>
          )}
        </div>

        <button
          className="settings-save-btn"
          onClick={handleSaveTimezone}
          disabled={tzSaving || !hasTzChanged}
          style={{
            width: '100%', padding: '0.875rem',
            background: tzSaved ? 'linear-gradient(135deg, #16a34a, #15803d)' : 'linear-gradient(135deg, #008081, #005f5f)',
            color: '#FFFFFF', border: 'none', borderRadius: '0.75rem',
            fontWeight: '700', fontSize: '0.95rem',
            cursor: (tzSaving || !hasTzChanged) ? 'not-allowed' : 'pointer',
            opacity: (tzSaving || !hasTzChanged) ? 0.5 : 1,
            boxShadow: (tzSaving || !hasTzChanged) ? 'none' : '0 4px 15px rgba(0,128,129,0.3)'
          }}
        >
          {tzSaved
            ? '✓ ' + tr('timezoneUpdated', 'Timezone updated')
            : tzSaving
              ? translate('saving')
              : tr('saveTimezone', 'Save timezone')}
        </button>
      </div>

      {/* ── Nickname card ── */}
      <div style={card}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {translate('changeNickname')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {translate('currentNicknameIs')} <strong style={{ color: colors.text }}>{userData?.name}</strong>.
        </p>

        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.875rem', backgroundColor: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.2)', borderRadius: '0.875rem', marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '1rem', flexShrink: 0 }}>🎭</span>
          <p style={{ fontSize: '0.8rem', color: '#facc15', lineHeight: 1.6, margin: 0 }}>
            {translate('keepAnonymous')}
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={sectionLabel}>{translate('newNickname')}</label>
          <input
            className="settings-input"
            type="text"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
            placeholder={translate('nicknamePlaceholder')}
            maxLength={20}
            style={{ ...inputStyle, borderColor: nameError ? '#f87171' : colors.inputBorder }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.35rem' }}>
            {nameError
              ? <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{nameError}</span>
              : <span />
            }
            <span style={{ fontSize: '0.75rem', color: newName.length > 18 ? '#f87171' : colors.counterColor }}>
              {newName.length}/20
            </span>
          </div>
        </div>

        <button
          className="settings-save-btn"
          onClick={handleSaveName}
          disabled={nameSaving || !newName.trim()}
          style={{
            width: '100%', padding: '0.875rem',
            background: nameSaved ? 'linear-gradient(135deg, #16a34a, #15803d)' : 'linear-gradient(135deg, #008081, #005f5f)',
            color: '#FFFFFF', border: 'none', borderRadius: '0.75rem',
            fontWeight: '700', fontSize: '0.95rem',
            cursor: (nameSaving || !newName.trim()) ? 'not-allowed' : 'pointer',
            opacity: (nameSaving || !newName.trim()) ? 0.5 : 1,
            boxShadow: (nameSaving || !newName.trim()) ? 'none' : '0 4px 15px rgba(0,128,129,0.3)'
          }}
        >
          {nameSaved ? '✓ ' + translate('nicknameUpdated') : nameSaving ? translate('saving') : translate('saveNickname')}
        </button>
      </div>

      {/* ── Goals achieved card ── */}
      <div style={card}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
          {translate('goalsAchievedTitle')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {translate('goalsPrivate')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1.25rem', backgroundColor: colors.goalsBg, borderRadius: '0.875rem', border: `1px solid ${colors.goalsBorder}` }}>
          <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, rgba(0,128,129,0.3), rgba(75,44,94,0.3))', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', flexShrink: 0 }}>🏆</div>
          <div>
            <div style={{ fontSize: '2.25rem', fontWeight: '800', color: '#00c8c8', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {goalsAchieved}
            </div>
            <div style={{ fontSize: '0.8rem', color: colors.goalsSubtext, marginTop: '0.2rem' }}>
              {translate('goalsCompletedCount', { count: goalsAchieved })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Legal links ── */}
      <div style={{ ...card, display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        <a href="/terms" className="settings-legal-link" style={{ fontSize: '0.8rem', color: colors.legalLinkColor, textDecoration: 'none' }}>{translate('termsOfService')}</a>
        <a href="/privacy" className="settings-legal-link" style={{ fontSize: '0.8rem', color: colors.legalLinkColor, textDecoration: 'none' }}>{translate('privacyPolicy')}</a>
        <a href="/refund" className="settings-legal-link" style={{ fontSize: '0.8rem', color: colors.legalLinkColor, textDecoration: 'none' }}>{translate('refundPolicy')}</a>
        <a href="mailto:support@veyl.space" className="settings-legal-link" style={{ fontSize: '0.8rem', color: colors.legalLinkColor, textDecoration: 'none' }}>{translate('contactSupport')}</a>
      </div>

      {/* ── Delete account card ── */}
      <div style={{ ...card, border: '1px solid rgba(224,92,92,0.2)' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f87171', marginBottom: '0.25rem' }}>
          {translate('deleteAccount')}
        </h3>
        <p style={{ fontSize: '0.8rem', color: colors.subtext, marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {translate('deleteAccountDesc')}
        </p>

        {!showDeleteConfirm ? (
          <button
            className="settings-delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
            style={{ width: '100%', padding: '0.875rem', backgroundColor: 'rgba(224,92,92,0.06)', color: '#f87171', border: '1px solid rgba(224,92,92,0.25)', borderRadius: '0.875rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            {translate('deleteMyAccountButton')}
          </button>
        ) : (
          <div>
            <div style={{ padding: '1rem', backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '0.875rem', marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.85rem', color: '#f87171', margin: 0, lineHeight: 1.6 }}>
                {translate('deleteWarning')}
              </p>
            </div>
            <input
              className="settings-input"
              type="text"
              value={deleteConfirmText}
              onChange={(e) => { setDeleteConfirmText(e.target.value); setDeleteError(''); }}
              placeholder={translate('typeDelete')}
              style={{ ...inputStyle, borderColor: 'rgba(224,92,92,0.4)', marginBottom: '0.75rem' }}
            />

            {usesPassword ? (
              <input
                className="settings-input"
                type="password"
                value={deletePassword}
                onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                placeholder="Enter your password to confirm"
                autoComplete="current-password"
                style={{ ...inputStyle, borderColor: 'rgba(224,92,92,0.4)', marginBottom: '0.75rem' }}
              />
            ) : (
              <p style={{ fontSize: '0.78rem', color: colors.subtext, margin: '0 0 0.75rem', lineHeight: 1.5 }}>
                You'll be asked to sign in with Google again to confirm it's you.
              </p>
            )}

            {deleteError && (
              <p style={{ fontSize: '0.8rem', color: '#f87171', margin: '0 0 0.75rem', textAlign: 'center', lineHeight: 1.5 }}>
                {deleteError}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeletePassword(''); setDeleteError(''); }}
                disabled={deleting}
                style={{ flex: 1, padding: '0.875rem', border: `1px solid ${colors.cancelBtnBorder}`, backgroundColor: 'transparent', color: colors.cancelBtnText, borderRadius: '0.875rem', fontWeight: '600', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.5 : 1 }}
              >
                {translate('cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting || (usesPassword && !deletePassword)}
                style={{ flex: 1, padding: '0.875rem', backgroundColor: '#E05C5C', color: '#FFFFFF', border: 'none', borderRadius: '0.875rem', fontWeight: '700', cursor: (deleteConfirmText !== 'DELETE' || deleting || (usesPassword && !deletePassword)) ? 'not-allowed' : 'pointer', opacity: (deleteConfirmText !== 'DELETE' || deleting || (usesPassword && !deletePassword)) ? 0.5 : 1, boxShadow: deleteConfirmText === 'DELETE' ? '0 4px 15px rgba(224,92,92,0.35)' : 'none' }}
              >
                {deleting ? translate('deleting2') : translate('confirmDelete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}